import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RESTRICTED_MODE_NOTICE_KEY, useAuth } from './../../context/auth-context.jsx';
import { isTenantRestrictedModeActiveError } from '../../lib/admin-api/http-client.js';
import { getRuntimeConfig } from '../../lib/runtime-config.js';
import { BrandedMark, normalizeBranding } from '../../shared/branding/branding.jsx';

function readInitialTheme() {
  try {
    return localStorage.getItem('app_theme') || document.documentElement.getAttribute('data-bs-theme') || 'light';
  } catch {
    return document.documentElement.getAttribute('data-bs-theme') || 'light';
  }
}

function clearRestrictedModeNotice() {
  try {
    sessionStorage.removeItem(RESTRICTED_MODE_NOTICE_KEY);
  } catch {
    // ignore storage errors
  }
}

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, completeTwoFactor, isAuthenticated } = useAuth();
  const branding = normalizeBranding(getRuntimeConfig().branding);

  const { t } = useTranslation();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorChallenge, setTwoFactorChallenge] = useState(null);
  const [error, setError] = useState('');
  const [restrictedNotice, setRestrictedNotice] = useState(() => {
    try {
      const hasNotice = sessionStorage.getItem(RESTRICTED_MODE_NOTICE_KEY) === '1';
      if (hasNotice) {
        sessionStorage.removeItem(RESTRICTED_MODE_NOTICE_KEY);
      }

      return hasNotice;
    } catch {
      return false;
    }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [theme, setTheme] = useState(readInitialTheme);

  useEffect(() => {
    const previousAttrs = {
      htmlLayout: document.documentElement.getAttribute('data-layout'),
      htmlTheme: document.documentElement.getAttribute('data-bs-theme'),
      htmlColorTheme: document.documentElement.getAttribute('data-color-theme'),
    };

    document.documentElement.setAttribute('data-layout', 'vertical');
    document.documentElement.setAttribute('data-color-theme', 'Blue_Theme');

    let link = document.getElementById('app-materialpro-css');
    let createdLink = false;
    if (!link) {
      link = document.createElement('link');
      link.id = 'app-materialpro-css';
      link.rel = 'stylesheet';
      link.href = '/assets/css/styles.css';
      document.head.appendChild(link);
      createdLink = true;
    }

    return () => {
      if (previousAttrs.htmlLayout) {
        document.documentElement.setAttribute('data-layout', previousAttrs.htmlLayout);
      } else {
        document.documentElement.removeAttribute('data-layout');
      }

      if (previousAttrs.htmlTheme) {
        document.documentElement.setAttribute('data-bs-theme', previousAttrs.htmlTheme);
      } else {
        document.documentElement.removeAttribute('data-bs-theme');
      }

      if (previousAttrs.htmlColorTheme) {
        document.documentElement.setAttribute('data-color-theme', previousAttrs.htmlColorTheme);
      } else {
        document.documentElement.removeAttribute('data-color-theme');
      }

      if (createdLink && link?.parentNode) {
        link.parentNode.removeChild(link);
      }
    };
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-bs-theme', theme);
    try {
      localStorage.setItem('app_theme', theme);
    } catch {
      // ignore storage errors
    }
  }, [theme]);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setRestrictedNotice(false);
    clearRestrictedModeNotice();
    setIsSubmitting(true);

    try {
      const result = await login({ identifier, password });
      if (result?.status === 'requires2fa') {
        setTwoFactorChallenge(result);
        setPassword('');
        setTwoFactorCode('');
        return;
      }
      navigate('/dashboard', { replace: true });
    } catch (requestError) {
      if (isTenantRestrictedModeActiveError(requestError)) {
        setRestrictedNotice(true);
        setError('');
      } else {
        setError(requestError.message || t('auth.authorizationFailed'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTwoFactorSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await completeTwoFactor({
        challengeId: twoFactorChallenge.challengeId,
        code: twoFactorCode,
      });
      navigate('/dashboard', { replace: true });
    } catch (requestError) {
      if (isTenantRestrictedModeActiveError(requestError)) {
        setTwoFactorChallenge(null);
        setTwoFactorCode('');
        setRestrictedNotice(true);
        setError('');
      } else {
        setError(requestError.message || t('auth.twoFactorInvalid'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToPassword = () => {
    setTwoFactorChallenge(null);
    setTwoFactorCode('');
    setError('');
    setRestrictedNotice(false);
    clearRestrictedModeNotice();
  };

  const handleThemeToggle = () => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  return (
    <div id="main-wrapper" className="auth-customizer-none">
      <div className="position-relative overflow-hidden radial-gradient min-vh-100 w-100 d-flex align-items-center justify-content-center">
        <div className="d-flex align-items-center justify-content-center w-100">
          <div className="row justify-content-center w-100">
            <div className="col-md-8 col-lg-6 col-xxl-3 auth-card">
              <div className="card mb-0 position-relative">
                <div className="card-body">
                  <button
                    type="button"
                    className="btn btn-sm app-icon-toggle app-login-theme-toggle"
                    onClick={handleThemeToggle}
                    aria-label={theme === 'dark' ? t('auth.switchToLight') : t('auth.switchToDark')}
                    title={theme === 'dark' ? t('auth.lightTheme') : t('auth.darkTheme')}
                  >
                    <i className={theme === 'dark' ? 'ti ti-sun' : 'ti ti-moon'} aria-hidden="true"></i>
                  </button>

                  <div className="text-nowrap logo-img d-flex align-items-center justify-content-center mb-4 w-100">
                    <BrandedMark branding={branding} imageClassName="crm-brand-logo-auth" imageHeight={52} />
                  </div>

                  {location.state?.passwordReset && (
                    <div className="alert alert-success py-2 mb-3">
                      {t('auth.passwordResetSuccess')}
                    </div>
                  )}
                  {restrictedNotice && (
                    <div className="alert alert-warning py-2 mb-3" role="alert">
                      {t('auth.restrictedModeNotice')}
                    </div>
                  )}

                  <form onSubmit={twoFactorChallenge ? handleTwoFactorSubmit : handleSubmit}>
                    {twoFactorChallenge ? (
                      <>
                        <div className="mb-3">
                          <h5 className="mb-1">{t('auth.twoFactorTitle')}</h5>
                          <p className="text-muted mb-0">{t('auth.twoFactorSubtitle')}</p>
                        </div>
                        <div className="mb-4">
                          <label htmlFor="twoFactorCode" className="form-label">{t('auth.twoFactorCodeLabel')}</label>
                          <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            className="form-control"
                            id="twoFactorCode"
                            value={twoFactorCode}
                            onChange={(event) => setTwoFactorCode(event.target.value)}
                            required
                          />
                        </div>
                      </>
                    ) : (
                      <>
                    <div className="mb-3">
                      <label htmlFor="identifier" className="form-label">{t('auth.usernameLabel')}</label>
                      <input
                        type="text"
                        className="form-control"
                        id="identifier"
                        placeholder={t('auth.usernamePlaceholder')}
                        value={identifier}
                        onChange={(event) => setIdentifier(event.target.value)}
                        required
                      />
                    </div>

                    <div className="mb-4">
                      <label htmlFor="password" className="form-label">{t('auth.passwordLabel')}</label>
                      <input
                        type="password"
                        className="form-control"
                        id="password"
                        placeholder={t('auth.passwordLabel')}
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        required
                      />
                    </div>

                    <div className="d-flex align-items-center justify-content-between mb-4">
                      <div className="form-check">
                        <input className="form-check-input primary" type="checkbox" value="" id="remember-device" defaultChecked />
                        <label className="form-check-label" htmlFor="remember-device">
                          {t('auth.rememberDevice')}
                        </label>
                      </div>
                      <Link className="text-primary fw-medium" to="/forgot-password">
                        {t('auth.forgotPassword')}
                      </Link>
                    </div>
                      </>
                    )}

                    {error && <div className="alert alert-danger py-2">{error}</div>}

                    <button type="submit" className="btn btn-primary w-100 py-8 mb-4" disabled={isSubmitting}>
                      {isSubmitting
                        ? t('auth.signingIn')
                        : twoFactorChallenge
                          ? t('auth.twoFactorVerify')
                          : t('auth.signIn')}
                    </button>
                    {twoFactorChallenge ? (
                      <button type="button" className="btn btn-outline-secondary w-100" onClick={handleBackToPassword} disabled={isSubmitting}>
                        {t('auth.backToSignIn')}
                      </button>
                    ) : null}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
